// Seed contracts for a fresh workspace: the real-world patterns people actually
// deploy on Bitcoin today, one per use case. An HTLC (payments and swaps), an
// M-of-N multisig (shared custody), and a tiered recovery vault (self-custody
// with loss and inheritance recovery). Each compiles to a live taproot address
// and certifies every spend path.

import type { VFile } from "../store";

const f = (path: string, content: string): VFile => ({
  path,
  content,
  language: path.endsWith(".sl") ? "seal" : path.endsWith(".json") ? "json" : "plaintext",
});

// Reusable BIP340 x-only public keys (valid curve points, from the test corpus).
const K1 = "0x2b4ea0a797a443d293ef5cff444f4979f06acfebd7e86d277475656138385b6c";
const K2 = "0x5cbdf0646e5db4eaa398f365f2ea7a0e3d419b7e0330e39ce92bddedcac4f9bc";
const K3 = "0xf28773c2d975288bc7d1d205c3748651b075fbc6610e58cddeeddf8f19405aa8";
const K4 = "0xf9308a019258c31049344f85f89d5229b531c845836f99b08601f113bce036f9";
const K5 = "0xdff1d77f2a671c5f36183726db2341be58feae1da2deced843240f7b502ba659";
const K6 = "0xdd308afec5777e13121fa72b9cc1b7cc0139715309b086c960e18fd969774eb8";

const HTLC = `// HTLC, a hash time-locked contract: the building block of cross-chain atomic
// swaps and Lightning payment routing (every routed Lightning payment is one of
// these). Funds leave one of two ways:
//
//   claim   the receiver knows the preimage of a payment hash, reveals it on
//           chain, and signs. Revealing the preimage is the whole point: it is
//           what lets an upstream hop pull their own incoming payment.
//   refund  if the preimage never appears, the sender reclaims the coins after
//           an absolute timeout, so funds are never stranded.
//
// The MuSig2 key path lets both sides settle cooperatively off chain with a
// single signature, indistinguishable from an ordinary payment, and fall back
// to the script paths only in a dispute.
contract Htlc {
    extern const claim_key:    PublicKey;        // receiver, claims by revealing the preimage
    extern const refund_key:   PublicKey;        // sender, reclaims after the timeout
    extern const payment_hash: Bytes<32>;        // SHA-256 of the secret preimage
    extern const timeout:      LockTime.Absolute;

    spend claim(preimage: Bytes<32>, sig: Signature) {
        require {
            sha256(preimage) == payment_hash,
            claim_key.check(sig)
        }
    }

    spend refund(sig: Signature) {
        require {
            after(timeout),
            refund_key.check(sig)
        }
    }

    keypath PublicKey.MuSig2([claim_key, refund_key]);
}
`;

const HTLC_ARGS = `{
  "claim_key": "${K2}",
  "refund_key": "${K1}",
  "payment_hash": "0xabababababababababababababababababababababababababababababababab",
  "timeout": { "height": 880000 }
}
`;

const MULTISIG = `// M-of-N multisig, the backbone of shared and institutional custody (the policy
// behind collaborative-custody wallets like Unchained, Casa and Nunchuk). The
// MuSig2 key path is a unanimous cooperative spend: one 65-WU signature, on
// chain indistinguishable from an ordinary payment. The script path is the
// M-of-N threshold fallback, for when the group cannot all sign together.
//
// The threshold is one comprehension: sum the valid signatures across the key
// set and require exactly M. It is == M, not >= M: with >= M a third party
// could strip an excess signature to a different valid witness, so == M is the
// non-malleable form. The spender supplies M signatures and declines the rest;
// which M is their choice.
contract Multisig {
    extern const M:    Int;
    extern const N:    Int;
    extern const keys: [PublicKey; N];

    require 1 <= M <= N;    // template precondition (instantiation-time)

    spend fallback(sigs: [Signature; N]) {
        require sum(k in keys, s in sigs => k.check(s)) == M;
    }

    keypath PublicKey.MuSig2(keys);
}
`;

const MULTISIG_ARGS = `{
  "M": 2,
  "N": 3,
  "keys": [
    "${K1}",
    "${K2}",
    "${K3}"
  ]
}
`;

const VAULT = `// Recovery vault, the policy behind self-custody wallets like Liana. Day to day
// you spend over the key path with your two primary keys, cheap and private,
// and every spend resets the recovery clocks. The script paths are safety nets
// that only open if those primary keys go quiet, so losing a key (or dying)
// never means losing the coins, while a thief who grabs one key still cannot
// move anything:
//
//   recover_soft   once the funds have sat untouched for the soft delay
//                  (~3 months), any 2 of 3 recovery keys -- you plus family
//                  members or a recovery service -- can move them.
//   recover_hard   after the hard delay (~1 year), a single offline cold-storage
//                  key is the last resort.
//
// An active owner always out-runs both timers; recovery is deliberately slow.
contract Vault {
    extern const primary_a:  PublicKey;          // your two everyday keys (2-of-2 key path)
    extern const primary_b:  PublicKey;
    extern const recovery:   [PublicKey; 3];     // three recovery keys, any 2 can act
    extern const cold:       PublicKey;          // final offline backup
    extern const soft_delay: LockTime.Relative;  // ~3 months of no movement
    extern const hard_delay: LockTime.Relative;  // ~1 year of no movement

    spend recover_soft(sigs: [Signature; 3]) {
        require {
            after(soft_delay),
            sum(k in recovery, s in sigs => k.check(s)) == 2
        }
    }

    spend recover_hard(sig: Signature) {
        require {
            after(hard_delay),
            cold.check(sig)
        }
    }

    keypath PublicKey.MuSig2([primary_a, primary_b]);
}
`;

const VAULT_ARGS = `{
  "primary_a": "${K1}",
  "primary_b": "${K2}",
  "recovery": ["${K3}", "${K4}", "${K5}"],
  "cold": "${K6}",
  "soft_delay": { "blocks": 12960 },
  "hard_delay": { "blocks": 52560 }
}
`;

export const SEED_FILES: Record<string, VFile> = Object.fromEntries(
  [
    f("htlc.sl", HTLC),
    f("htlc.args.json", HTLC_ARGS),
    f("multisig.sl", MULTISIG),
    f("multisig.args.json", MULTISIG_ARGS),
    f("vault.sl", VAULT),
    f("vault.args.json", VAULT_ARGS),
  ].map((v) => [v.path, v]),
);

/** Starter content for a brand-new contract. */
export const NEW_CONTRACT_TEMPLATE = `contract Untitled {
    extern const owner: PublicKey;

    spend main(signature: Signature) {
        require owner.check(signature);
    }

    keypath None;
}
`;
